/**
 * یک رخداد و همه‌ی پرچم‌هایش — و اصلاحِ همان رخداد، همان‌جا.
 *
 * ═══ چرا پرچم‌ها بعد از اصلاح می‌مانند ═══
 *
 * خواسته‌ی صریح: «رویداد اصلاح شود، ولی گزارش بماند». ذخیره فقط پرچم‌های
 * تیک‌خورده را «تاییدشده و اصلاح‌شده» می‌کند (`fixed_at` + ستون‌ها) و هیچ
 * سطری پاک نمی‌شود. همین است که آمارِ «چند گزارش درست بود و به اصلاح رسید»
 * را ممکن می‌کند.
 *
 * ═══ چرا فقط ستونِ عوض‌شده فرستاده می‌شود ═══
 *
 * `buildFixPatch` هر فیلدی را که دست نخورده کنار می‌گذارد. پس تابعِ SQL فقط
 * همان‌ها را قفل می‌کند (کرالر دیگر رویشان نمی‌نویسد) و بقیه‌ی رخداد هر شب
 * مثل قبل تازه می‌شود.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { opsRepo } from '@/core/data/opsRepo';
import { formatDateTime } from '@/core/lib/dates';
import { reportError } from '@/core/lib/report';
import { tagLabel } from '@/core/lib/tagLabel';
import type { Tag } from '@/core/domain/types';
import {
  FIELD_COLUMNS, FIELD_LABEL, KIND_ICON, STATUS_LABEL,
  buildFixPatch, draftOf, fixProblems, isActionable, issueStatus, parseEventIssue,
  rowKey, suggestTagIds, tagForLabel,
  type EventIssueEvent, type EventIssueRow, type FixColumn, type FixDraft,
} from '@/core/lib/eventIssuesReport';
import { Modal } from '@/ui/Modal';

type TextCol = Exclude<FixColumn, 'is_free'>;

/** ستون → فیلدِ پرچم، برای نشانِ «N پرچم» کنارِ هر کادر. */
const COLUMN_FIELDS: Record<string, string[]> = {};
for (const [field, cols] of Object.entries(FIELD_COLUMNS)) {
  for (const c of cols) (COLUMN_FIELDS[c] ??= []).push(field);
}

/** ستونی که مقدارِ درستِ کاربر را مستقیم می‌پذیرد (متنِ ساده، نه زمان/تگ). */
const FIX_TARGET: Record<string, TextCol> = {
  title: 'title', image: 'image_url', description: 'description', where: 'address',
  price: 'price_note', ticket: 'ticket_url', link: 'url',
};

const FIELDS: { col: TextCol; label: string; multiline?: boolean; ltr?: boolean; type?: string }[] = [
  { col: 'title', label: 'عنوان' },
  { col: 'description', label: 'توضیحات', multiline: true },
  { col: 'starts_at', label: 'شروع', type: 'datetime-local' },
  { col: 'ends_at', label: 'پایان', type: 'datetime-local' },
  { col: 'price_note', label: 'قیمت (متن)' },
  { col: 'address', label: 'نشانی' },
  { col: 'url', label: 'لینکِ منبع', ltr: true },
  { col: 'ticket_url', label: 'لینکِ خریدِ بلیط', ltr: true },
  { col: 'image_url', label: 'عکس (آدرس)', ltr: true },
];

export function FixPanel({ event, rows, tags, onClose }: {
  event: EventIssueEvent;
  rows: EventIssueRow[];
  tags: Tag[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<FixDraft>(() => draftOf(event));
  const [picked, setPicked] = useState<Set<string>>(
    () => new Set(rows.filter((r) => isActionable(r) && issueStatus(r) === 'open').map(rowKey)));
  const [note, setNote] = useState('');
  const [tagQuery, setTagQuery] = useState('');
  const [done, setDone] = useState<{ changed: string[]; locked: string[] } | null>(null);

  const parsed = useMemo(() => rows.map((r) => ({ r, p: parseEventIssue(r.text) })), [rows]);
  const reportsOn = (col: string) => {
    const fields = COLUMN_FIELDS[col] ?? [];
    return parsed.filter(({ p }) => p.field && fields.includes(p.field));
  };

  const plan = buildFixPatch(event, draft);
  const problems = fixProblems(draft);
  const issueIds = rows.filter((r) => isActionable(r) && picked.has(rowKey(r))).map((r) => r.issue_id!);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['eventIssues'] });

  const save = useMutation({
    mutationFn: () => opsRepo.fixEvent(event.id, plan.patch, plan.tagIds, issueIds, note),
    onSuccess: (r) => { setDone(r); setPicked(new Set()); setNote(''); invalidate(); },
    onError: (err) => reportError('admin.eventIssues.fix', err),
  });
  const review = useMutation({
    mutationFn: (a: { ids: number[]; verdict: 'accepted' | 'rejected' | 'open' }) =>
      opsRepo.reviewEventIssues(a.ids, a.verdict, note),
    onSuccess: invalidate,
    onError: (err) => reportError('admin.eventIssues.review', err),
  });

  const set = <K extends keyof FixDraft>(k: K, v: FixDraft[K]) => { setDone(null); setDraft((d) => ({ ...d, [k]: v })); };
  const toggleTag = (id: number) =>
    set('tag_ids', draft.tag_ids.includes(id) ? draft.tag_ids.filter((x) => x !== id) : [...draft.tag_ids, id]);
  const togglePick = (k: string) => setPicked((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  const tagRows = parsed.filter(({ p }) => p.field === 'tag');
  const suggested = suggestTagIds(event.tag_ids, tagRows.map(({ r }) => r), tags);
  const sameAsSuggested = [...suggested].sort().join() === [...draft.tag_ids].sort().join();
  const tagById = new Map(tags.map((t) => [t.id, t]));
  const reportedTagIds = new Set(tagRows.map(({ p }) => tagForLabel(tags, p.value)?.id).filter((x): x is number => x != null));
  const needle = tagQuery.trim().toLocaleLowerCase();
  const tagChoices = tags.filter((t) => !needle || tagLabel(t).toLocaleLowerCase().includes(needle)
    || t.labelEn.toLocaleLowerCase().includes(needle));

  const busy = save.isPending || review.isPending;

  return (
    <Modal wide title={`#${event.short_id ?? '?'} — ${event.title}`}
      hint="پرچم‌ها بالا، اصلاحِ رخداد پایین. ذخیره پرچم‌های تیک‌خورده را «تاییدشده و اصلاح‌شده» می‌کند؛ هیچ پرچمی پاک نمی‌شود."
      onClose={onClose}>
      <div className="chiprow">
        <span className="chip">{event.status}</span>
        {event.source ? <span className="chip mono">{event.source}</span> : null}
        {event.city ? <span className="chip">{event.city}</span> : null}
        <span className="chip">{event.crawler ? 'کرالری — اصلاح قفل می‌شود' : 'غیرِکرالری'}</span>
        {event.url ? <a className="chip" href={event.url} target="_blank" rel="noreferrer">صفحه‌ی منبع ↗</a> : null}
        {event.locked_fields.length ? (
          <span className="chip mono" title="کرالر این ستون‌ها را بازنویسی نمی‌کند">🔒 {event.locked_fields.join(', ')}</span>
        ) : null}
      </div>

      <h3 style={{ marginTop: 12 }}>پرچم‌ها ({rows.length})</h3>
      <div className="scrollx" style={{ maxHeight: 260 }}>
        <table className="tbl">
          <tbody>
            {parsed.map(({ r, p }) => {
              const st = issueStatus(r);
              const live = isActionable(r);
              return (
                <tr key={rowKey(r)}>
                  <td>
                    <input type="checkbox" disabled={!live} checked={live && picked.has(rowKey(r))}
                      title="با ذخیره، تاییدشده و اصلاح‌شده علامت بخورد"
                      onChange={() => togglePick(rowKey(r))} />
                  </td>
                  <td>{KIND_ICON[p.kind]} {p.summary}</td>
                  <td className="dim">{r.who ?? 'بی‌نام'}{r.internal ? ' · تستی' : ''}</td>
                  <td className="dim">{formatDateTime(r.at)}</td>
                  <td>
                    <span className={`chip iss-${st}`}>{STATUS_LABEL[st]}</span>
                    {r.fixed_at ? <span className="chip on" title={r.fixed_fields.join(', ')}>اصلاح شد</span> : null}
                    {r.withdrawn ? <span className="chip">کاربر پس گرفت</span> : null}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {live && p.field && FIX_TARGET[p.field] && p.value ? (
                      <button type="button" className="linkbtn" onClick={() => set(FIX_TARGET[p.field!], p.value!)}>
                        بگذارش در فرم
                      </button>
                    ) : null}
                    {live && p.field && FIX_TARGET[p.field] && p.value ? ' · ' : null}
                    {live ? (
                      <>
                        {st !== 'accepted' ? (
                          <button type="button" className="linkbtn" disabled={busy}
                            onClick={() => review.mutate({ ids: [r.issue_id!], verdict: 'accepted' })}>تایید</button>
                        ) : null}
                        {st === 'open' ? ' · ' : null}
                        {st !== 'rejected' ? (
                          <button type="button" className="linkbtn" disabled={busy}
                            onClick={() => review.mutate({ ids: [r.issue_id!], verdict: 'rejected' })}>رد</button>
                        ) : null}
                        {st !== 'open' ? (
                          <>{' · '}<button type="button" className="linkbtn" disabled={busy}
                            onClick={() => review.mutate({ ids: [r.issue_id!], verdict: 'open' })}>باز کن</button></>
                        ) : null}
                      </>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h3 style={{ marginTop: 14 }}>اصلاحِ رخداد</h3>
      <div className="iss-form">
        {FIELDS.map((f) => {
          const n = reportsOn(f.col).length;
          const value = draft[f.col] as string;
          const changed = f.col in plan.patch;
          return (
            <label key={f.col} className={`iss-field${changed ? ' changed' : ''}`}>
              <span className="k">
                {f.label}
                {n ? <span className="chip warn" style={{ marginInlineStart: 6 }}>{n} پرچم</span> : null}
                {event.locked_fields.includes(f.col) ? ' 🔒' : ''}
              </span>
              {f.multiline ? (
                <textarea className="input textarea" value={value} rows={6}
                  onChange={(e) => set(f.col, e.target.value)} />
              ) : (
                <input className="input" value={value} type={f.type ?? 'text'} dir={f.ltr ? 'ltr' : undefined}
                  onChange={(e) => set(f.col, e.target.value)} />
              )}
            </label>
          );
        })}
        <label className={`iss-field${'is_free' in plan.patch ? ' changed' : ''}`}>
          <span className="k">
            رایگان
            {reportsOn('is_free').length ? <span className="chip warn" style={{ marginInlineStart: 6 }}>{reportsOn('is_free').length} پرچم</span> : null}
          </span>
          <span><input type="checkbox" checked={draft.is_free} onChange={(e) => set('is_free', e.target.checked)} /> رایگان است</span>
        </label>
      </div>
      <p className="dim" style={{ fontSize: 12 }}>
        زمان‌ها به وقتِ همین مرورگرند. عکسِ تازه اگر در فهرستِ عکس‌های نامعتبر باشد، باز هم پنهان می‌ماند.
      </p>

      <div className={`iss-field${plan.tagIds ? ' changed' : ''}`} style={{ marginTop: 8 }}>
        <span className="k">
          تگ‌ها
          {tagRows.length ? <span className="chip warn" style={{ marginInlineStart: 6 }}>{tagRows.length} پرچم</span> : null}
        </span>
        <div className="chiprow">
          {draft.tag_ids.map((id) => (
            <button key={id} type="button" className={`chip on${reportedTagIds.has(id) ? ' warn' : ''}`}
              onClick={() => toggleTag(id)} title="بردار">
              {tagById.get(id) ? tagLabel(tagById.get(id)!) : `#${id}`} ✕
            </button>
          ))}
          {!draft.tag_ids.length ? <span className="dim">بی تگ</span> : null}
        </div>
        {tagRows.length && !sameAsSuggested ? (
          <p className="note" style={{ marginTop: 6 }}>
            پیشنهاد از روی پرچم‌ها: {suggested.map((id) => (tagById.get(id) ? tagLabel(tagById.get(id)!) : `#${id}`)).join('، ') || 'بی تگ'}
            {' '}<button type="button" className="linkbtn" onClick={() => set('tag_ids', suggested)}>اعمالش کن</button>
          </p>
        ) : null}
        {tagRows.some(({ p }) => !tagForLabel(tags, p.value)) ? (
          <p className="dim" style={{ fontSize: 12 }}>
            یکی از تگ‌های گفته‌شده در فهرستِ تگ‌ها پیدا نشد (شاید کاربر خودش نوشته یا برچسبش عوض شده) — دستی انتخاب کن.
          </p>
        ) : null}
        <input className="input" placeholder="افزودنِ تگ — جستجو" value={tagQuery}
          onChange={(e) => setTagQuery(e.target.value)} style={{ marginTop: 6 }} />
        {needle ? (
          <div className="chiprow" style={{ marginTop: 6, maxHeight: 120, overflow: 'auto' }}>
            {tagChoices.filter((t) => !draft.tag_ids.includes(t.id)).slice(0, 40).map((t) => (
              <button key={t.id} type="button" className={`chip${reportedTagIds.has(t.id) ? ' warn' : ''}`}
                onClick={() => toggleTag(t.id)}>+ {tagLabel(t)}</button>
            ))}
          </div>
        ) : null}
        <p className="dim" style={{ fontSize: 12 }}>
          ⚠ تگ‌ها قفل نمی‌شوند: اگر کرالر تگِ ژانرِ این رخداد را دوباره بنویسد، این اصلاح برمی‌گردد.
        </p>
      </div>

      <textarea className="input textarea" value={note} placeholder="یادداشت (اختیاری) — در دفترِ کارها و کنارِ پرچم‌ها می‌ماند"
        onChange={(e) => setNote(e.target.value)} style={{ marginTop: 8, minHeight: 50 }} />

      {problems.map((p) => <p key={p} className="err">{p}</p>)}
      {save.error ? <p className="err">{String((save.error as Error).message ?? save.error)}</p> : null}
      {review.error ? <p className="err">{String((review.error as Error).message ?? review.error)}</p> : null}
      {done ? (
        <p className="note">
          ذخیره شد: {done.changed.join('، ') || 'چیزی عوض نشد'}
          {done.locked.length ? ` · قفل شد: ${done.locked.join('، ')}` : ''}
        </p>
      ) : null}

      <div className="chiprow" style={{ marginTop: 8 }}>
        <button type="button" className="btn" disabled={busy || problems.length > 0 || plan.changed.length === 0}
          onClick={() => save.mutate()}>
          {save.isPending ? '…' : issueIds.length
            ? `ذخیره + تاییدِ ${issueIds.length} پرچم`
            : 'ذخیره'}
        </button>
        <button type="button" className="btn ghost" disabled={busy} onClick={() => { setDraft(draftOf(event)); setDone(null); }}>
          برگرداندنِ فرم
        </button>
        <span className="dim">
          {plan.changed.length ? `عوض‌شده: ${plan.changed.map((c) => (c === 'tags' ? 'تگ‌ها' : c)).join('، ')}` : 'هنوز چیزی عوض نشده'}
        </span>
      </div>
      <p className="dim" style={{ fontSize: 12 }}>
        فیلدهای پرچم‌خورده: {[...new Set(parsed.map(({ p }) => p.field).filter(Boolean))].map((f) => FIELD_LABEL[f!] ?? f).join('، ') || '—'}
      </p>
    </Modal>
  );
}
